import { CustomButton } from "@/components/ui/Buttons";
import { Dispatch, SetStateAction, useState } from "react";
import LeftArrowShort from "@/assets/icons/authentication/LeftArrowShort.svg";
import Skip from "@/assets/icons/authentication/Skip.svg";

// outfit image
import FormalImg from "@/assets/images/auth/otufits/formal.png";
import SemiFormalImg from "@/assets/images/auth/otufits/semi-formal.png";
import CasualImg from "@/assets/images/auth/otufits/casual.png";
import TraditionalImg from "@/assets/images/auth/otufits/taditionals.png";
import UploadOutfitBackground from "@/components/ui/UploadOutfitBackground";

// background images
import CleanWallImg from "@/assets/images/auth/backgrounds/clean-wall.png";
import CouchImg from "@/assets/images/auth/backgrounds/couch.png";
import IndoorImg from "@/assets/images/auth/backgrounds/indoor.png";
import Outdoor from "@/assets/images/auth/backgrounds/outdor.png";

import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import {
  resetApplicationForm,
  updateV2Field,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import { handleApiRequest } from "@/utils/handleApiRequest";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useGetMeQuery } from "@/redux/feature/user/userApi";
import { useSubmitApplicationMutation } from "@/redux/feature/creatorApplication/creatorApplicationApi";
import { SkipToast } from "./Skiptoast";

const outfits = [
  { name: "Formals", image: FormalImg },
  { name: "Semi-Formals", image: SemiFormalImg },
  { name: "Casuals", image: CasualImg },
  { name: "Traditionals", image: TraditionalImg },
];

const backgrounds = [
  { name: "Clean Wall", image: CleanWallImg },
  { name: "Couch", image: CouchImg },
  { name: "Indoor", image: IndoorImg },
  { name: "Outdoor", image: Outdoor },
];

type ApplicationStep6Props = {
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
};

const ApplicationStep6 = ({
  currentStep,
  setCurrentStep,
}: ApplicationStep6Props) => {
  const dispatch = useAppDispatch();
  const [showSkipToast, setShowSkipToast] = useState(false);

  const { v2Extras } = useAppSelector((s) => s.signUpV2);

  const navigate = useNavigate();

  const { SubmitApplicationInfo } = useAppSelector((s) => s.signUpV2);
  const { user } = useAppSelector((state) => state.auth);

  const { data } = useGetMeQuery(user?.email);
  const userId = data?.data?._id;

  const [submitApplication, { isLoading }] = useSubmitApplicationMutation();
  const mapTaggedFile = (x: any) => ({
    name: x.name,
    size: x.size,
    type: x.type,
    live_url: x.live_url,
    hash: x.hash,
    tag: x.tag ?? "",
    otherTagText: x.otherTagText ?? "",
  });

  const transformFormData = (data: any) => ({
    email: user?.email,
    fullName: data.fullName,
    gender: data.gender,
    age: Number(data.age),
    phoneNumber: data.phoneNumber,
    country: data.country,
    city: data.city,
    state: data.state,
    addressWithZipCode: data.address,
    skinTone: data.skinTone,
    bodyType: data.bodyType,
    hasRelativesInVideo: data.hasRelativesInVideo,
    user_id: userId,
    brandsDontWantToWorkWith: data.brandsDontWantToWorkWith,
    profileLink: data.profileLink,
    experience: data.experience,
    experienceDescription: data.experienceDescription,
    videoLinks: data.videoLinks,
    topics: data.topics,
    skills: data.skills,
    availableHoursPerWeek: data.availableHoursPerWeek,
    availabilityForWeekendsOrEvenings: data.weekendAvailability,
    languagesSpoken: data.language,
    lastDrawnSalary: `${data.lastSalary}`,
    expectedPay: `${data.expectedPay}`,
    wardrobeImages: data.wardrobeStyle,
    backgroundImages: data.backgroundStyle,
    videoEquipment: {
      propsAvailable: data?.propsAvailable,
      greenScreenSpecs: data.greenScreenDetails,
      noiselessRoomAndMicrophoneSpecs: data.audioEquipmentDetails,
      cameraOrSmartphoneSpecs: data.cameraDetails,
      lightingSpecs: data.lightingDetails,
    },
    additionalInfo: data.additionalExperience,
  });

  const handleSubmitApplication = async () => {
    const cleanedProfileLinks = (v2Extras.profileLinks ?? []).filter(
      (item) => item.url && item.url.trim() !== "",
    );
    const transformedData = transformFormData(SubmitApplicationInfo);
    const payload = {
      ...transformedData,
      ...v2Extras,

      languagesSpoken: v2Extras.languages.map((item) => item.name).join(", "),
      wardrobeImages: (v2Extras.wardrobeImages ?? []).map(mapTaggedFile),
      profilePhoto: v2Extras.profilePhoto?.live_url,
      backgroundImages: (v2Extras.backgroundImages ?? []).map(mapTaggedFile),
      profileLinks: cleanedProfileLinks,
      skills: [
        ...(v2Extras.skillsMulti ?? []),
        ...(v2Extras.customSkills ?? []),
      ].join(", "),
    };
    console.log(payload);
    // merge into one
    await handleApiRequest({
      payload: payload,
      requestFunction: submitApplication,
      onSuccess: () => {
        toast.success("Application submitted successfully!");
        dispatch(resetApplicationForm());
        navigate("/dashboard");
      },
      onError: (err) => {
        const error = err as {
          data?: { errorSource?: { path?: string; message: string }[] };
        };

        if (error?.data?.errorSource?.length) {
          error.data.errorSource.forEach((item) => {
            toast.error(item.message);
          });
        } else {
          toast.error("Submission failed. Please try again.");
        }
      },
    });
  };
  return (
    <>
      <div>
        {/* header */}
        <div className="mb-8">
          <div className="flex items-center gap-x-2 ">
            <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
              <p className="text-white text-[11px] leading-5 tracking-normal">
                Step 6 of 5
              </p>
            </div>
            <h1 className="text-[#060B13] text-xl tracking-[-0.7px] font-medium">
              Outfits & Backgrounds
            </h1>
          </div>
          <p className="text-[#475467] text-xs tracking-[-0.5%] mt-2">
            Add outfit and background options you already have. This helps
            brands plan shoots faster.
          </p>
        </div>

        <div className="space-y-8">
          <div>
            <h6 className="tracking-normal text-sm">
              <span className="text-[#121217] font-medium">
                Add outfit options{" "}
              </span>
              <span className="text-[#475467]">
                (Upload 3-4 images of outfits you can wear on camera.)
              </span>
            </h6>

            <p className="text-[#475467] text-[10px] tracking-normal mt-1">
              Formals, business casual, casuals, traditional — phone photos work
              fine.
            </p>

            {/* Samples */}
            <div className="gap-2 mb-5 mt-5">
              <p className="text-[#475467] text-[10px] tracking-normal mb-2">
                Samples :
              </p>
              <div className="flex gap-4">
                {outfits.map((outfit) => (
                  <div key={outfit.name} className="flex flex-col items-center">
                    <div className="w-[66px] lg:w-[75px] mb-1 relative">
                      <img
                        src={outfit.image}
                        alt={outfit.name}
                        className="object-cover rounded-lg"
                      />
                    </div>
                    <div className="bg-[#F0F0F0] py-1 px-1 rounded w-[66px] lg:w-[75px]">
                      <p className="text-[10px] text-[#475467] tracking-normal text-center">
                        {outfit.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <UploadOutfitBackground
              mode="outfit"
              useExternalState
              externalFiles={v2Extras.wardrobeImages ?? []}
              onExternalFilesChange={(files) =>
                dispatch(updateV2Field("wardrobeImages", files))
              }
              useLiveUrl
            />
          </div>
          <div>
            <h6 className="tracking-normal text-sm">
              <span className="text-[#121217] font-medium">
                Add background options{" "}
              </span>
              <span className="text-[#475467]">
                (Upload 3-4 images of outfits you can wear on camera.)
              </span>
            </h6>

            <p className="text-[#475467] text-[10px] tracking-normal mt-1">
              Simple wall, aesthetic setup, couch, kitchen, balcony, greenery.
            </p>

            {/* Samples */}
            <div className="gap-2 mb-5 mt-5">
              <p className="text-[#475467] text-[10px] tracking-normal mb-2">
                Samples :
              </p>
              <div className="flex gap-4">
                {backgrounds.map((background) => (
                  <div
                    key={background.name}
                    className="flex flex-col items-center"
                  >
                    <div className="w-[66px] lg:w-[75px] mb-1 relative">
                      <img
                        src={background.image}
                        alt={background.name}
                        className="object-cover rounded-lg"
                      />
                    </div>
                    <div className="bg-[#F0F0F0] py-1 px-1 rounded w-[66px] lg:w-[75px]">
                      <p className="text-[10px] text-[#475467] tracking-normal text-center">
                        {background.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <UploadOutfitBackground
              mode="background"
              useExternalState
              externalFiles={v2Extras.backgroundImages ?? []}
              onExternalFilesChange={(files) =>
                dispatch(updateV2Field("backgroundImages", files))
              }
              useLiveUrl
            />
          </div>
        </div>

        {/* action button */}
        <div className="mt-[50px] flex items-center justify-end gap-1 md:gap-2 lg:gap-3">
          <CustomButton
            variant="outline"
            border="1px solid #E4E4E4"
            fontSize="14px"
            fontWeight="600"
            color="#4D4D4D"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px] flex items-center gap-x-1.5"
            onClick={() => setCurrentStep((prev) => prev - 1)}
          >
            <LeftArrowShort />
            Go Back
          </CustomButton>
          <CustomButton
            variant="outline"
            border="1px solid #E4E4E4"
            fontSize="14px"
            fontWeight="600"
            color="#4D4D4D"
            padding="5px 10.5px"
            borderRadius="200px"
            onClick={() => {
              setShowSkipToast(true);
              setTimeout(async () => {
                await handleSubmitApplication();
              }, 2000);
            }}
            className="tracking-[-0.11px] flex items-center gap-x-1.5"
          >
            <Skip />
            Skip
          </CustomButton>
          <CustomButton
            backgroundColor="#FF6600"
            fontSize="14px"
            fontWeight="600"
            color="white"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px]"
            onClick={handleSubmitApplication}
            disabled={isLoading}
          >
            {isLoading ? "Submitting..." : "Submit Application"}
          </CustomButton>
        </div>
      </div>
      <SkipToast
        visible={showSkipToast}
        onClose={() => setShowSkipToast(false)}
      />
    </>
  );
};

export default ApplicationStep6;
